import React, { useState } from 'react';
import { Mail, Lock, LogIn } from 'lucide-react';
import chatApi from '../../../services/chatApi';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';

/**
 * Inline secure login form rendered inside a chat bubble.
 * Password field is real <input type="password"> — never posted to chat
 * history. On success, calls onAction({type:'auth_success', user, messages}).
 */
const LoginFormCard = ({ data, onAction }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [forgotMode, setForgotMode] = useState(false);
  const auth = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      const res = await chatApi.loginChat(email, password);
      const { token, user, forma_messages } = res.data;
      localStorage.setItem('hpme_token', token);
      // propagate into AuthContext by reload-free path — call login hook
      auth?.loginFromResponse?.(token, user);
      onAction?.({ type: 'auth_success', payload: { user, messages: forma_messages || [] } });
    } catch (err) {
      setError(err.response?.data?.detail || 'Sign in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      await chatApi.forgotPassword(email);
      toast.success('If that email is registered, we sent you a reset link');
      onAction?.({ type: 'forgot_sent', payload: { email } });
    } catch (err) {
      setError('Could not send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (forgotMode) {
    return (
      <form onSubmit={handleForgot} className="space-y-3 rounded-sm border border-zinc-700 bg-zinc-900/60 p-4" data-testid="card-forgot-password">
        <p className="text-sm text-zinc-300">No worries — what's your email?</p>
        <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-700 rounded-sm px-3 py-2">
          <Mail className="w-4 h-4 text-zinc-500" />
          <input type="email" className="bg-transparent outline-none flex-1 text-sm text-white" placeholder="you@email.com"
            value={email} onChange={e => setEmail(e.target.value)} required data-testid="forgot-email-input" />
        </div>
        {error && <p className="text-red-400 text-xs">{error}</p>}
        <div className="flex gap-2">
          <button type="submit" disabled={loading} className="flex-1 bg-primary text-black font-secondary font-bold py-2 rounded-sm disabled:opacity-50 text-sm" data-testid="forgot-submit-btn">
            {loading ? 'Sending…' : 'Send reset link'}
          </button>
          <button type="button" onClick={() => setForgotMode(false)} className="text-sm text-zinc-400 hover:text-white px-3" data-testid="forgot-back-btn">Back</button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-sm border border-zinc-700 bg-zinc-900/60 p-4" data-testid="card-login-form">
      <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-700 rounded-sm px-3 py-2">
        <Mail className="w-4 h-4 text-zinc-500" />
        <input type="email" className="bg-transparent outline-none flex-1 text-sm text-white" placeholder="Email"
          value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" required data-testid="login-email-input" />
      </div>
      <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-700 rounded-sm px-3 py-2">
        <Lock className="w-4 h-4 text-zinc-500" />
        <input type="password" className="bg-transparent outline-none flex-1 text-sm text-white" placeholder="Password"
          value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" required data-testid="login-password-input" />
      </div>
      {error && <p className="text-red-400 text-xs" data-testid="login-error">{error}</p>}
      <div className="flex items-center justify-between">
        <button type="submit" disabled={loading}
          className="flex-1 flex items-center justify-center gap-2 bg-primary text-black font-secondary font-bold py-2 rounded-sm disabled:opacity-50 text-sm"
          data-testid="login-submit-btn">
          <LogIn className="w-4 h-4" />{loading ? 'Signing in…' : 'Sign In'}
        </button>
      </div>
      <button type="button" onClick={() => { setError(null); setForgotMode(true); }}
        className="w-full text-center text-xs text-zinc-400 hover:text-primary transition-colors pt-1"
        data-testid="forgot-password-link">
        Forgot password?
      </button>
    </form>
  );
};

export default LoginFormCard;
